import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
/* eslint-disable react-hooks/exhaustive-deps */
import { languages } from 'monaco-editor/esm/vs/editor/editor.api';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { suricataTokenizer } from '../../tokenProviders';
import { tsvTokenizer } from '../../tokenProviders/tsv';

export type IMonacoEditor = typeof monaco;
export interface MonacoEditorProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /**
   * width of editor.
   * Defaults to `100%`
   */
  width?: number | string;
  /**
   * height of editor.
   * Defaults to `100%`.
   */
  height?: number | string;
  /**
   * value of the auto created model in the editor.
   */
  value?: string;
  /**
   * the initial value of the auto created model in the editor.
   */
  defaultValue?: string;
  /**
   * The initial language of the auto created model in the editor.
   * To not create automatically a model, use `model: null`.
   */
  language?: monaco.editor.IStandaloneEditorConstructionOptions['language'];
  /**
   * User provided extension function provider for auto-complete.
   */
  autoComplete?: (
    model: monaco.editor.ITextModel,
    position: monaco.Position,
  ) => languages.CompletionItem[];
  /**
   * Initial theme to be used for rendering.
   * The current out-of-the-box available themes are: 'vs' (default), 'vs-dark', 'hc-black'.
   * You can create custom themes via `monaco.editor.defineTheme`.
   * To switch a theme, use `monaco.editor.setTheme`
   */
  theme?: monaco.editor.IStandaloneEditorConstructionOptions['theme'];
  /**
   * The options to create an editor.
   */
  options?: monaco.editor.IStandaloneEditorConstructionOptions;
  /**
   * an event emitted when the editor has been mounted (similar to `componentDidMount` of React)
   */
  editorDidMount?: (
    editor: monaco.editor.IStandaloneCodeEditor,
    monaco: IMonacoEditor,
  ) => void;
  /**
   * an event emitted when the content of the current model has changed.
   */
  onChange?: (
    value: string,
    event: monaco.editor.IModelContentChangedEvent,
  ) => void;
}

export interface IRefEditorInstance {
  container: HTMLDivElement | null;
  editor?: monaco.editor.IStandaloneCodeEditor;
  monaco: IMonacoEditor;
}

const MonacoEditorImpl = (
  {
    width = '100%',
    height = '100%',
    value: _value = '',
    theme = '',
    language = 'javascript',
    autoComplete,
    options = {},
    editorDidMount,
    onChange,
    defaultValue = '',
    ...other
  }: MonacoEditorProps,
  ref:
    | ((instance: IRefEditorInstance) => void)
    | React.RefObject<IRefEditorInstance>
    | null
    | undefined,
) => {
  options.language = language || options.language;
  options.theme = theme || options.theme;
  const [val] = useState(defaultValue);
  const container = useRef<HTMLDivElement>(null);
  const editor = useRef<monaco.editor.IStandaloneCodeEditor>();

  // let the parent have access to the container/editor refs and monaco instance
  useImperativeHandle(ref, () => ({
    container: container.current,
    editor: editor.current,
    monaco,
  }));

  // mount the editor
  useEffect(() => {
    if (container.current) {
      // initialize the editor, this will render the HTML and create the editor instance
      editor.current = monaco.editor.create(container.current, {
        value: val,
        language,
        ...options,
      });
      // set the editor theme after its mounted
      if (options.theme) {
        monaco.editor.setTheme(options.theme);
      }
      // After initializing monaco editor
      editorDidMount?.(editor.current, monaco);

      // hook up the onChange callback to get triggered by the editor change events
      editor.current.onDidChangeModelContent((event) => {
        const valueCurrent = editor.current?.getValue() ?? '';
        // Always refer to the latest value
        onChange?.(valueCurrent, event);
      });

      // registers suricata syntax highlighting
      monaco.languages.register({ id: 'suricata' });
      monaco.languages.setMonarchTokensProvider('suricata', suricataTokenizer);
      // registers tsv syntax highlighting
      monaco.languages.register({ id: 'tsv' });
      monaco.languages.setMonarchTokensProvider('tsv', tsvTokenizer);
    }

    // destroy the editor
    return () => {
      if (editor.current) {
        editor.current.dispose();
      }
    };
  }, []);

  // update the theme
  useEffect(() => {
    if (options.theme) {
      monaco.editor.setTheme(options.theme);
    }
  }, [options.theme]);

  // enable custom autoCompletion functions to work, skip if autoComplete is not given
  useEffect(() => {
    let CPDisposable: monaco.IDisposable;
    if (editor.current && autoComplete) {
      if (editor?.current?.getModel() && editor?.current?.getPosition()) {
        CPDisposable = monaco.languages.registerCompletionItemProvider(
          language,
          {
            provideCompletionItems: (model, position) => {
              return {
                suggestions: autoComplete(model, position),
              };
            },
          },
        );
      }
    }
    return () => {
      CPDisposable && CPDisposable.dispose();
    };
  }, [language, autoComplete]);

  // update the editor's language when changed if the editor has already been created
  useEffect(() => {
    if (editor.current) {
      const model = editor.current.getModel();
      if (model) {
        monaco.editor.setModelLanguage(model, language || '');
      }
    }
  }, [language]);

  useEffect(() => {
    // if we detect a change to `insertSpaces`, we reindent the document.
    // Note: Of the various ways I've tested, these actions (convert tabs to x)
    // are more reliable than actions like reindent document.
    const oldOptions = editor.current?.getModel()?.getOptions();
    if (oldOptions?.insertSpaces !== options.insertSpaces) {
      const action =
        options.insertSpaces === true
          ? 'editor.action.indentationToSpaces'
          : 'editor.action.indentationToTabs';
      editor.current?.getAction(action)?.run();
    }

    // then update the options
    editor.current?.updateOptions(options);
  }, [JSON.stringify(options)]);

  // actually render the editor, the div gets to have the container ref which
  // creates the editor for us
  return (
    <div {...other} ref={container} style={{ ...other.style, width, height }} />
  );
};

export const MonacoEditor = React.forwardRef<
  IRefEditorInstance,
  MonacoEditorProps
>(MonacoEditorImpl);
